/**
 * UndoToast Component
 *
 * Bottom-right toast for block/unblock undo actions. (A-08, SCRUM-1143)
 *
 * Variants:
 *  - success: dark bg-gray-900, message + Undo button (text-[#4A7C23])
 *  - error:   bg-red-700, error message only (no Undo button)
 *
 * Spec styling:
 *  - Position: fixed bottom-4 right-4 z-50
 *  - Container: bg-gray-900 text-white rounded-lg px-4 py-3 flex items-center gap-4 shadow-lg
 *  - Undo button: text-[#4A7C23] font-medium hover:underline
 *  - Error container: bg-red-700 text-white rounded-lg px-4 py-3
 *
 * @param {Object} props
 * @param {string} props.message - Toast message to display
 * @param {'success'|'error'} [props.variant='success'] - Toast variant
 * @param {Function} [props.onUndo] - Called when user clicks Undo
 * @param {Function} [props.onDismiss] - Called when toast should be dismissed
 */

import React from 'react';

export default function UndoToast({ message, variant = 'success', onUndo, onDismiss }) {
  const isError = variant === 'error';

  return (
    <div
      data-testid="undo-toast"
      className={`
        fixed bottom-4 right-4 z-50
        ${isError ? 'bg-red-700' : 'bg-gray-900'}
        text-white rounded-lg px-4 py-3 shadow-lg
        flex items-center gap-4
        animate-fade-in
      `.replace(/\s+/g, ' ').trim()}
      role="status"
      aria-live="polite"
    >
      <span className="text-sm">{message}</span>

      {!isError && onUndo && (
        <button
          type="button"
          onClick={onUndo}
          className="text-[#4A7C23] font-medium hover:underline text-sm flex-shrink-0"
          data-testid="undo-button"
        >
          Undo
        </button>
      )}

      {(isError || !onUndo) && onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          className="text-white/70 hover:text-white text-sm flex-shrink-0"
          data-testid="dismiss-button"
          aria-label="Dismiss"
        >
          ✕
        </button>
      )}
    </div>
  );
}
